// @flow

import React, { useEffect, useMemo, useRef, useState } from "react";
import Select, { createFilter, components, type RSTypes } from "react-select";
import debounce from "lodash.debounce";
import { MultiSelectOption, MultiSelectValue } from "./components.js";
import http from "@http";
import config from "@config";
import { OPERATORS, transformFilters } from "@transformers/request";

export type OptionType = string | number | boolean | null | { value: any, label: string };

export type ValueOptionType = OptionType;
type NormalizedOptionType = { value: any, label: string };
type Props = $Exact<{
  options: Array<OptionType>,
  value?: ValueOptionType | Array<ValueOptionType>,
  defaultValue?: ValueOptionType | Array<ValueOptionType>, // TODO - it's not used in the code and need to be removed
  onChange?: Function,
  disabled?: boolean,
  readOnly?: boolean,
  className?: string,
  classNamePrefix?: string,
  isSearchable?: boolean,
  placeholder?: string,
  autoFocus?: boolean,
  id?: string,
  isMulti?: boolean,
  isExpanded?: boolean,
  withoutPortal?: boolean,
  withoutEmptyOption?: boolean,
  menuIsOpen?: boolean,
  menuPortalTarget?: HTMLElement,
  menuPlacement?: "bottom" | "auto" | "top",
  ...RSTypes,
}>;

const customStyles = {
  // https://react-select.com/styles#style-object
  menuPortal: (provided: Object) => ({
    ...provided,
    zIndex: 1060,
  }),
  option: (provided: Object, state: Object) => {
    return {
      ...provided,
      color: state.label?.indexOf && state.label.indexOf(" - Inactive") !== -1 ? "red" : "#6F6F6F", //Sorry but AU-7433
    };
  },
};

const DEBOUNCE_MS = 250;

function centerFocusedOption(menuList: HTMLDivElement) {
  const focusedOption = menuList.querySelector('[class*="__option--is-focused"]');

  if (!(focusedOption instanceof HTMLElement)) {
    return false;
  }

  const menuRect = menuList.getBoundingClientRect();
  const optionRect = focusedOption.getBoundingClientRect();
  const centeredScrollTop =
    menuList.scrollTop + optionRect.top - menuRect.top - (menuRect.height - optionRect.height) / 2;
  const maxScrollTop = menuList.scrollHeight - menuList.clientHeight;

  menuList.scrollTop = Math.max(0, Math.min(centeredScrollTop, maxScrollTop));

  return true;
}

function CenteredMenuList(props: Object) {
  const menuListRef = useRef<?HTMLDivElement>(null);
  const hasCenteredRef = useRef<boolean>(false);

  function setMenuListRef(node: ?HTMLDivElement) {
    menuListRef.current = node;

    if (typeof props.innerRef === "function") {
      props.innerRef(node);
    }
  }

  useEffect(() => {
    if (props.selectProps?.isMulti || hasCenteredRef.current || !menuListRef.current) {
      return;
    }

    const animationFrame = window.requestAnimationFrame(() => {
      if (menuListRef.current && centerFocusedOption(menuListRef.current)) {
        hasCenteredRef.current = true;
      }
    });

    return () => window.cancelAnimationFrame(animationFrame);
  }, [props.children, props.focusedOption, props.selectProps?.isMulti]);

  return <components.MenuList {...props} innerRef={setMenuListRef} />;
}

function ComboBox(props: Props) {
  const remoteOptionsUrl = props.asyncOptions?.optionsUrl;

  const [remoteOptions, setRemoteOptions] = useState<Array<OptionType>>([]);
  const [inputValue, setInputValue] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);

  const requestIdRef = useRef<number>(0);
  const fetchOptionsRef = useRef<(query: string, nextPage?: number) => Promise<void>>();

  const isInitialMount = useRef(true);

  const debouncedFetch = useMemo(
    () =>
      debounce((query: string) => {
        setPage(1);
        setHasMore(true);
        fetchOptionsRef.current && fetchOptionsRef.current(query, 1);
      }, DEBOUNCE_MS),
    [],
  );

  useEffect(() => {
    if (!remoteOptionsUrl) {
      return;
    }

    if (isInitialMount.current) {
      isInitialMount.current = false;
      fetchOptions("", 1);
      return;
    }

    debouncedFetch(inputValue);

    return () => debouncedFetch.cancel();
  }, [inputValue, remoteOptionsUrl, debouncedFetch]);

  useEffect(() => () => debouncedFetch.cancel(), [debouncedFetch]);

  fetchOptionsRef.current = fetchOptions;

  const menuPortalTarget = props.hasOwnProperty("menuPortalTarget")
    ? props.menuPortalTarget
    : document.body;

  function getOptionValue(option) {
    if (option !== null && typeof option === "object") {
      if ("value" in option) {
        return option.value;
      }
      if ("id" in option) {
        return option.id;
      }
    }

    return option;
  }

  function handleChange(selectedOption: OptionType | Array<OptionType>) {
    if (props.isMulti && Array.isArray(selectedOption)) {
      return (
        // $FlowIgnore
        props.onChange && props.onChange(selectedOption.map((opt: OptionType) => opt.value))
      );
    } else {
      setInputValue("");
      // $FlowIgnore
      props.onSelect && props.onSelect(selectedOption);
      // $FlowIgnore
      return props.onChange && props.onChange(selectedOption?.value);
    }
  }

  function options(): Array<NormalizedOptionType> {
    const source = remoteOptionsUrl ? remoteOptions : props.options || [];
    const opts = source.map(opt => {
      if (opt !== null && typeof opt === "object") {
        let label;
        let value;

        if (opt.hasOwnProperty("value")) {
          value = opt.value;
        } else if (opt.hasOwnProperty("id")) {
          value = opt.id;
        } else {
          value = "";
        }

        if (opt.hasOwnProperty("label")) {
          label = opt.label;
        } else if (opt.hasOwnProperty("name")) {
          label = opt.name;
        } else {
          label = value;
        }

        return { ...opt, label: label, value: value };
      }
      return {
        label: `${opt}`,
        value: opt,
      };
    });
    const selectedOptionValue = getOptionValue(props.value);

    if (
      opts &&
      !props.isMulti &&
      !props.withoutEmptyOption &&
      !opts.find(opt => opt.value === selectedOptionValue)
    ) {
      return [{ label: "", value: null }, ...opts];
    }
    return opts || [];
  }

  function normalizeValueOption(val) {
    let value = null;
    let label = String(value || "");

    if ("value" in val) {
      value = val.value;
    } else if ("id" in val) {
      value = val.id;
    }

    if ("label" in val) {
      label = val.label;
    } else if ("name" in val) {
      label = val.name;
    }

    return { ...val, value, label };
  }

  function selectedValue(opts: Array<NormalizedOptionType>) {
    if (props.isMulti && Array.isArray(props.value)) {
      return props.value.map(val => {
        const option = opts.find(el => el.value === getOptionValue(val));

        if (option) return option;
        if (typeof val == "object" && val !== null) return normalizeValueOption(val);
        else
          return {
            label: `${val}`,
            value: val,
          };
      });
    } else if (typeof props.value === "object" && props.value !== null) {
      const option = opts.find(el => el.value === getOptionValue(props.value));

      if (option) return option;
      return normalizeValueOption(props.value);
    } else {
      // $FlowIgnore
      return opts.find(el => el.value === props.value);
    }
  }

  function mapResponseToOptions(data) {
    const { asyncOptions } = props;
    const valueKey = asyncOptions.optionValueKey || "id";
    const labelKeys = asyncOptions.labelKeys || [];

    return data.map(item => {
      const label = labelKeys
        .map(fieldKey => (item && item[fieldKey] ? String(item[fieldKey]) : ""))
        .filter(Boolean)
        .join(" ");

      const value = item ? item[valueKey] : null;
      return {
        ...item,
        value,
        label: label || String(value || ""),
      };
    });
  }

  async function fetchOptions(query: string, nextPage?: number = 1): Promise<void> {
    const { asyncOptions } = props;
    const limit = asyncOptions.limit || config.resultsPerPage;
    const requestId = ++requestIdRef.current;
    setIsLoading(true);
    try {
      const response = await http.get(asyncOptions.optionsUrl, {
        params: {
          sort: asyncOptions.sort,
          page: nextPage,
          limit,
          search: transformFilters(
            { [asyncOptions.searchField]: query, ...asyncOptions.searchFilters },
            { [asyncOptions.searchField]: OPERATORS.LIKE, ...asyncOptions.searchOperators },
          ),
          ...asyncOptions.params,
        },
      });

      if (requestId !== requestIdRef.current) {
        return;
      }

      const items = response?.data?.content || response?.data?.results || response?.data || [];
      const mappedItems = mapResponseToOptions(Array.isArray(items) ? items : []);
      const totalElements = response?.data?.totalElements || 0;

      if (nextPage === 1) {
        setRemoteOptions(mappedItems);
      } else {
        setRemoteOptions(prev => [...prev, ...mappedItems]);
      }

      setPage(nextPage);
      setHasMore(totalElements > nextPage * limit);
    } catch (e) {
      if (requestId === requestIdRef.current && nextPage === 1) {
        setRemoteOptions([]);
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setIsLoading(false);
      }
    }
  }

  const opts = options();
  const value = selectedValue(opts);

  return (
    <Select
      classNamePrefix={props.classNamePrefix || "combo-box"}
      menuIsOpen={props.isExpanded}
      className={`combo-box-root ${(props.readOnly && "readonly") || ""} ${props.className || ""}`}
      styles={customStyles}
      backspaceRemovesValue={false}
      isDisabled={props.disabled}
      readOnly={props.readOnly}
      onChange={handleChange}
      value={value}
      options={opts}
      inputId={props.id}
      name={props.name}
      autoFocus={props.autoFocus}
      isMulti={props.isMulti || false}
      isClearable={false}
      isSearchable={props.isSearchable}
      placeholder={props.placeholder}
      closeMenuOnSelect={!props.isMulti}
      hideSelectedOptions={!props.isMulti}
      menuPortalTarget={!props.withoutPortal ? menuPortalTarget : null}
      menuPlacement="auto"
      menuShouldScrollIntoView={true}
      maxMenuHeight={200}
      components={{
        MenuList: CenteredMenuList,
        Option: props.isMulti ? MultiSelectOption : components.Option,
        MultiValueContainer: MultiSelectValue,
      }}
      isLoading={remoteOptionsUrl ? isLoading : false}
      onMenuScrollToBottom={() => {
        if (remoteOptionsUrl && hasMore && !isLoading) {
          fetchOptions(inputValue, page + 1);
        }
      }}
      onInputChange={(val, meta) => {
        if (meta.action === "input-change") {
          setInputValue(val);
        }
        return val;
      }}
    />
  );
}

export default ComboBox;
